import React, { useState, useRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Field } from 'formik'
import { isEmpty, startCase } from 'lodash'
import { IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import { TextArea } from '@/components/forms/formik'
import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'
import { generatePrompt, evaluatePrompt } from '@/reducers/playbooks/playbook.actions'

const MAX_PROMPT_LENGTH = 1500

export const PromptEditor = ({
  name,
  value,
  generateContent,
  section,
  readOnly,
  setFieldValue,
  onLoadingChange,
  onInsufficientData,
  onFocus,
  onPromptCommitted,
  placeholder: customPlaceholder,
}) => {
  const dispatch = useDispatch()
  const [isGenerating, setIsGenerating] = useState(false)
  const [isEvaluating, setIsEvaluating] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const lastEvaluatedPrompt = useRef(null)

  const trimmedValue = value?.trim() || ''
  const isLoading = isGenerating || isEvaluating
  const disabled = readOnly || isLoading

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading)
    }
  }, [isLoading])

  const handleEvaluate = async (promptOverride) => {
    const promptToEvaluate = promptOverride || trimmedValue

    if (!promptToEvaluate) {
      setFeedback(null)
      return
    }

    if (promptToEvaluate === lastEvaluatedPrompt.current) {
      return
    }

    if (onPromptCommitted) {
      onPromptCommitted(promptToEvaluate)
    }

    setIsEvaluating(true)
    try {
      const result = await dispatch(evaluatePrompt(promptToEvaluate))
      if (result) {
        lastEvaluatedPrompt.current = promptToEvaluate
        setFeedback(result)
      }
    } finally {
      setIsEvaluating(false)
    }
  }

  const handleGenerate = async () => {
    if (!generateContent) {
      return
    }

    setIsGenerating(true)
    try {
      const result = await dispatch(generatePrompt(generateContent, section))
      setIsGenerating(false)
      if (result?.insufficientData) {
        if (onInsufficientData) {
          onInsufficientData()
        }
      } else if (result) {
        setFieldValue(name, result)
        await handleEvaluate(result)
      }
    } catch {
      setIsGenerating(false)
    }
  }

  useEffect(() => {
    if (!trimmedValue && generateContent) {
      handleGenerate()
    }
  }, [])

  return (
    <>
      <Field
        name={name}
        component={TextArea}
        disabled={disabled}
        placeholder={
          isGenerating
            ? 'Generating prompt...'
            : customPlaceholder ||
              'Describe what the agent should say or do for this item to check off.'
        }
        rows={2}
        maxLength={MAX_PROMPT_LENGTH}
        autoResize
        onFocus={() => {
          if (onFocus) {
            onFocus()
          }
        }}
        onBlur={() => {
          handleEvaluate()
        }}
      />
      <div className="form-caption text-right">
        {value?.length || 0} / {MAX_PROMPT_LENGTH}
      </div>
      {(isGenerating || isEvaluating) && (
        <div className="flex-align-center small-gap mt">
          <Loader size="small" />
          <span>{isGenerating ? 'Generating initial prompt...' : 'Evaluating prompt...'}</span>
        </div>
      )}
      {feedback && !isEvaluating && (
        <>
          {isEmpty(feedback.warnings) ? (
            <Banner success icon={<IconCircleCheck />}>
              Prompt looks good
            </Banner>
          ) : (
            <>
              <Banner success>
                <strong>Enhanced Prompt:</strong> {feedback.improved_rule}{' '}
                <Button
                  link
                  onClick={() => {
                    const improvedRule = feedback.improved_rule.slice(0, MAX_PROMPT_LENGTH)
                    setFieldValue(name, improvedRule)
                    setFeedback(null)
                    lastEvaluatedPrompt.current = null
                    if (onPromptCommitted) {
                      onPromptCommitted(improvedRule)
                    }
                  }}
                >
                  Click here to apply
                </Button>
              </Banner>
              <Banner warning>
                {feedback.warnings.map((warning, index) => (
                  <div
                    className="flex-start medium-gap mbp"
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${warning.type}-${index}`}
                  >
                    <IconAlertTriangle className="icon-svg" />
                    <div>
                      <strong>{startCase(warning.type)}:</strong> <span>{warning.detail}</span>
                    </div>
                  </div>
                ))}
              </Banner>
            </>
          )}
        </>
      )}
    </>
  )
}
